import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Megaphone, MessageSquare, Phone, Mail, Smartphone, BellRing, ShieldCheck, Users, Layers } from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Campaign Messaging Platform — SMS, USSD, WhatsApp, Email & Voice campaigns" },
      { name: "description", content: "Run large-scale multi-channel messaging campaigns with audience segmentation, approval workflows, and real-time delivery analytics." },
      { property: "og:title", content: "Campaign Messaging Platform" },
      { property: "og:description", content: "SMS, USSD, WhatsApp, Email, Voice & Push campaigns at scale." },
    ],
  }),
  component: Landing,
});

const channels = [
  { icon: MessageSquare, label: "SMS" },
  { icon: Phone, label: "USSD" },
  { icon: MessageSquare, label: "WhatsApp" },
  { icon: Mail, label: "Email" },
  { icon: Phone, label: "Voice" },
  { icon: BellRing, label: "Push" },
];

function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
              <Megaphone className="h-4 w-4" />
            </div>
            <span className="font-semibold">CMP</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth"><Button variant="ghost">Sign in</Button></Link>
            <Link to="/auth"><Button>Get started</Button></Link>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-24 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground mb-6">
          <ShieldCheck className="h-3 w-3" /> Enterprise-grade · Africa's Talking powered
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
          Reach millions across <span className="text-primary">every channel</span>.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
          A cloud platform for SMS, USSD, WhatsApp, Email, Voice, and Push campaigns — with audience
          segmentation, approval workflows, and real-time delivery analytics.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link to="/auth"><Button size="lg">Launch your first campaign</Button></Link>
        </div>

        <div className="mt-16 grid grid-cols-3 md:grid-cols-6 gap-4">
          {channels.map((c) => (
            <div key={c.label} className="rounded-lg border border-border bg-card p-4 flex flex-col items-center gap-2">
              <c.icon className="h-5 w-5 text-primary" />
              <span className="text-xs text-muted-foreground">{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-card/40">
        <div className="mx-auto max-w-6xl px-6 py-20 grid md:grid-cols-3 gap-8">
          <Feature icon={Users} title="Audience CRM" body="Manage contacts with rich demographic, geographic, and behavioural attributes." />
          <Feature icon={Layers} title="Smart segmentation" body="Build precise audiences using county, ward, language, tags, and custom fields." />
          <Feature icon={Smartphone} title="Multi-channel" body="One platform, six channels. Right message, right person, right channel." />
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-6 py-8 text-sm text-muted-foreground flex justify-between">
          <span>© {new Date().getFullYear()} Campaign Messaging Platform</span>
          <Link to="/auth" className="hover:text-foreground">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, body }: { icon: any; title: string; body: string }) {
  return (
    <div>
      <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1">{body}</p>
    </div>
  );
}
